/* eslint-disable brace-style */

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthConfig, OAuthEvent, OAuthService } from 'angular-oauth2-oidc';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  ReplaySubject,
} from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { DPUser } from '../models/DPUser';
import { AuthServiceInterface } from './auth.service.interface';
import { UtilsService } from './utils.service';
import { SentryUserService } from '../../shared/services/sentry-user.service';

/**
 * Authentication service.
 */
@Injectable({ providedIn: 'root' })
export class AuthService implements AuthServiceInterface {
  // Subject holding authenticated status
  private isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);

  // Key to store return url in local storage for redirection after login
  private returnUrlKey = 'auth_return_url';

  // Obvservable resolving authenticated status
  public isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();

  // Subject holding loading status of authentication data
  private isDoneLoadingSubject$ = new ReplaySubject<boolean>();

  // Subject resolving loading status of authentication data
  public isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();

  // Emits the driverID whenever it changes
  public hasTokenRefreshed$ = new BehaviorSubject<boolean>(false);

  /**
   * Publishes `true` if and only if (a) all the asynchronous initial
   * login calls have completed or errorred, and (b) the user ended up
   * being authenticated.
   *
   * In essence, it combines:
   *
   * - the latest known state of whether the user is authorized
   * - whether the ajax calls for initial log in have all been done
   */
  public canActivateProtectedRoutes$: Observable<boolean> = combineLatest([
    this.isAuthenticated$,
    this.isDoneLoading$,
  ]).pipe(map((values) => values.every((b) => b)));

  // private navigateToLoginPage() {}

  constructor(
    private oauthService: OAuthService,
    private router: Router,
    private utils: UtilsService,
    private sentryUserService: SentryUserService
  ) {
    // Useful for debugging:
    this.oauthService.events.subscribe((event) => {
      // if (event instanceof OAuthErrorEvent) {
      //   console.error('OAuthErrorEvent Object:', event);
      // } else {
      //   console.warn('OAuthEvent Object:', event);
      // }

      if (event.type === 'token_refreshed') {
        const user = this.getUserInfo();
        console.log('token_refreshed', user);
        this.hasTokenRefreshed$.next(true);
      }

      this.isAuthenticatedSubject$.next(
        this.oauthService.hasValidAccessToken()
      );
    });

    this.oauthService.events
      .pipe(filter((e) => e.type === 'user_profile_loaded'))
      .subscribe(() => {
        const matches = location.href.match(/\/\/([^\.]+).leetrans.com/);

        // Check the tenant ID.
        // Tenant ID is the subdomain name and if it does not match,
        // log out the user because it's invalid login
        if (
          (matches && matches[1] === this.Tenant) ||
          location.host === 'localhost:4200'
        ) {
          this.isDoneLoadingSubject$.next(true);
          // Set user context for error tracking
          const user = this.getUserInfo();
          this.sentryUserService.setUser(user);

          // Set organizational context for multi-tenant tracking
          if (user?.CompanyID && user?.Tenant) {
            this.sentryUserService.setOrganizationContext(
              parseInt(user.CompanyID, 10),
              user.Tenant,
              {
                companySettingId: user.CompanySettingID,
                driverStatus: user.DriverStatus,
              }
            );
          }
        } else {
          this.logout();
        }
      });

    // this.oauthService.events
    //   .pipe(
    //     filter((e) => ['session_terminated', 'session_error'].includes(e.type))
    //   )
    //   .subscribe((e) => this.navigateToLoginPage());
  }

  /**
   * Initiate oauth authentication process.
   * It authenticates user if logged in. Otherwise it redirects user to oauth login screen.
   */
  public runInitialLoginSequence(): void {
    const url = new URL(location.href);
    const inviteID = url.pathname.match(/^\/auth\/?$/)
      ? url.searchParams.get('inviteid')
      : '';

    const orderId = url.pathname.match(/^\/auth\/?$/)
      ? url.searchParams.get('orderId')
      : '';

    const disableAccountCreation = url.pathname.match(/^\/auth(?:\/|\/login)?$/)
      ? url.searchParams.get('disableAccountCreation')
      : '';

    this.oauthService.configure(
      this.authConfig(inviteID, orderId, disableAccountCreation)
    );
    this.oauthService
      .loadDiscoveryDocumentAndLogin()
      .then(() => {
        this.oauthService.loadUserProfile().then(() => {
          const returnUrl = this.getAndClearReturnUrl();
          if (returnUrl) {
            this.router.navigateByUrl(returnUrl);
          }
        });
      })
      .catch(() => this.isDoneLoadingSubject$.next(true));
  }

  public login(targetUrl?: string) {
    if (targetUrl) {
      this.setReturnUrl(targetUrl);
    }
    this.oauthService.initLoginFlow(targetUrl || this.router.url);
  }

  public logout() {
    // Clear user context from error tracking
    this.sentryUserService.setUser(null);
    this.oauthService.logOut();
  }

  public revokeTokenAndLogout() {
    // Clear user context from error tracking
    this.sentryUserService.setUser(null);
    this.oauthService.revokeTokenAndLogout();
  }

  public refresh(): Promise<OAuthEvent> {
    return this.oauthService.silentRefresh();
  }

  // Store return URL
  public setReturnUrl(url: string) {
    if (!url.includes('auth/login')) {
      localStorage.setItem(this.returnUrlKey, url);
    }
  }

  // Get and clear return URL
  public getAndClearReturnUrl(): string {
    const url = localStorage.getItem(this.returnUrlKey);
    localStorage.removeItem(this.returnUrlKey);
    return url;
  }

  public loadUserProfile() {
    return this.oauthService.loadUserProfile();
  }

  public hasValidToken() {
    return this.oauthService.hasValidAccessToken();
  }

  public getUserInfo() {
    const user = this.oauthService.getIdentityClaims() as DPUser;
    if (user) {
      user.HasAccessedDashboard = (user.HasAccessedDashboard as any) === 'True';
      user.HasLoggedIn = (user.HasLoggedIn as any) === 'True';
    }

    return user;
  }

  public get userName() {
    const user = this.getUserInfo();
    return `${user?.FirstName || ''} ${user?.LastName || ''}`.trim();
  }

  public get userID() {
    const user = this.getUserInfo();
    return user?.UserID || '';
  }

  public get driverID() {
    const user = this.getUserInfo();
    return user?.DriverID || '';
  }

  public get driverStatus() {
    const user = this.getUserInfo();
    return user?.DriverStatus || '';
  }

  public get Tenant() {
    const user = this.getUserInfo();
    return user?.Tenant || '';
  }

  public get HireDate() {
    const user = this.getUserInfo();
    return user?.HireDate ? new Date(user.HireDate) : null;
  }

  public get token() {
    return this.oauthService.getAccessToken();
  }

  public get companySettingID() {
    const user = this.getUserInfo();
    return parseInt(user?.CompanySettingID || '0', 10);
  }

  public get companyID() {
    const user = this.getUserInfo();
    return parseInt(user?.CompanyID || '0', 10);
  }

  public isValidUser() {
    return (
      this.driverID &&
      (this.driverStatus === 'Y' ||
        this.driverStatus === 'A' ||
        this.driverStatus === 'P')
    );
  }

  public isNewOrPendingUser() {
    return !this.driverID;
  }

  public isReapplyingUser() {
    return (
      this.driverStatus === 'T' ||
      this.driverStatus === 'N' ||
      this.driverStatus === 'R'
    );
  }

  get getRedirectUrl() {
    console.log(
      window.location.origin + (this.getAndClearReturnUrl() || '/auth/login')
    );
    return (
      window.location.origin + (this.getAndClearReturnUrl() || '/auth/login')
    );
  }

  private authConfig(
    inviteID: string,
    orderId: string,
    disableAccountCreation: string
  ) {
    const companyDomain = this.utils.getSubdomain();
    const config: AuthConfig = {
      // OpenIddict normalises the issuer to Uri.AbsoluteUri, which carries a trailing slash
      // (IdentityServer4 emitted it without one). angular-oauth2-oidc compares the discovery
      // document's issuer and the id_token's iss strictly, so the expected value must match
      // byte-for-byte. authBaseURL stays slash-less because other services concatenate paths onto it.
      issuer: environment.authBaseURL + '/',
      clientId: 'DriverPortalApp', // The "Auth Code + PKCE" client
      responseType: 'code',
      redirectUri: window.location.origin + '/auth/login',
      scope:
        'openid profile offline_access DriverPortalApi.read IdentityUserApi', // Ask offline_access to support refresh token refreshes
      sessionChecksEnabled: false,
      showDebugInformation: true, // Also requires enabling "Verbose" level in devtools
      clearHashAfterLogin: false, // https://github.com/manfredsteyer/angular-oauth2-oidc/issues/457#issuecomment-431807040,
      nonceStateSeparator: 'semicolon', // Real semicolon gets mangled by IdentityServer's URI encoding
      customQueryParams: {
        subdomain: companyDomain,
      },
    };

    if (inviteID) {
      config.customQueryParams = {
        ...config.customQueryParams,
        inviteID,
      };
    }

    if (orderId) {
      config.customQueryParams = {
        ...config.customQueryParams,
        orderId,
      };
    }

    if (disableAccountCreation) {
      config.customQueryParams = {
        ...config.customQueryParams,
        disableAccountCreation,
      };
    }

    return config;
  }
}
